import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, ShoppingBag, Users, Image, Film, Settings, LogOut, Menu, X, Star, BarChart3 } from "lucide-react";
import { clearToken, getToken } from "@/lib/adminApi";
import { Logo } from "@/components/common/Logo";
import { DashboardHome } from "@/components/admin/DashboardHome";
import { AnalyticsPanel } from "@/components/admin/AnalyticsPanel";
import { OrdersPanel } from "@/components/admin/OrdersPanel";
import { CustomersPanel } from "@/components/admin/CustomersPanel";
import { ProductsPanel } from "@/components/admin/ProductsPanel";
import { MediaPanel } from "@/components/admin/MediaPanel";
import { ReviewsPanel } from "@/components/admin/ReviewsPanel";
import { SettingsPanel } from "@/components/admin/SettingsPanel";
import { ErrorBoundary } from "@/components/admin/ErrorBoundary";

const NAV = [
  ["dashboard", "Home", LayoutDashboard, DashboardHome],
  ["orders", "Orders", ShoppingBag, OrdersPanel],
  ["customers", "Customers", Users, CustomersPanel],
  ["analytics", "Analytics", BarChart3, AnalyticsPanel],
  ["products", "Product Photos", Image, ProductsPanel],
  ["media", "Media Manager", Film, MediaPanel],
  ["reviews", "Reviews", Star, ReviewsPanel],
  ["settings", "Settings", Settings, SettingsPanel],
];

export default function Admin() {
  const nav = useNavigate();
  const [active, setActive] = useState("dashboard");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { if (!getToken()) nav("/admin/login"); }, [nav]);

  // Don't mount any panels (which fire authed API calls) until we have a token.
  if (!getToken()) return null;

  const logout = () => { clearToken(); nav("/admin/login"); };
  const Panel = NAV.find((n) => n[0] === active)?.[3] || AnalyticsPanel;

  const NavLinks = () => (
    <nav className="space-y-1">
      {NAV.map(([key, label, Icon]) => (
        <button key={key} onClick={() => { setActive(key); setMobileOpen(false); }} data-testid={`nav-${key}`}
          className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-sm transition-colors ${active === key ? "bg-[#232222] text-[#FFFCFA]" : "text-[#6E6168] hover:bg-[#F2E8E6]"}`}>
          <Icon className="h-4 w-4" /> {label}
        </button>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#FFFCFA]" data-testid="admin-dashboard">
      {/* Mobile header */}
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-[#F2E8E6] bg-[#FFFCFA]/95 px-5 py-3 backdrop-blur-xl lg:hidden">
        <button onClick={() => setMobileOpen(true)}><Menu className="h-5 w-5 text-[#2C2B2C]" /></button>
        <Logo to="/" />
        <button onClick={logout}><LogOut className="h-5 w-5 text-[#8A7F86]" /></button>
      </header>

      <div className="mx-auto flex max-w-[1500px]">
        {/* Sidebar (desktop) */}
        <aside className="sticky top-0 hidden h-screen w-64 flex-none flex-col border-r border-[#F2E8E6] p-5 lg:flex">
          <div className="px-2 pb-6"><Logo to="/" /></div>
          <NavLinks />
          <button onClick={logout} data-testid="admin-logout" className="mt-auto flex items-center gap-3 rounded-2xl px-4 py-3 text-sm text-[#8A7F86] transition-colors hover:bg-[#F2E8E6]">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </aside>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="fixed inset-0 z-50 flex lg:hidden" onClick={() => setMobileOpen(false)}>
            <div className="h-full w-64 bg-[#FFFCFA] p-5" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-2 pb-6"><Logo to="/" /><button onClick={() => setMobileOpen(false)}><X className="h-5 w-5" /></button></div>
              <NavLinks />
            </div>
          </div>
        )}

        <main className="min-w-0 flex-1 px-5 py-8 md:px-10">
          <ErrorBoundary resetKey={active}>
            {active === "dashboard" ? <DashboardHome onGoOrders={() => setActive("orders")} /> : <Panel />}
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
